import { createContext, useCallback, useContext } from "react";

export interface EmptyRewards {
  isContentEmpty: boolean;
  isSurveyEmpty: boolean;
  isReviewEmpty: boolean;
}

export const EmptyRewardsContext =
  createContext<{
    emptyRewards?: Partial<EmptyRewards>;
    setEmptyRewards(
      updater: <T extends Partial<EmptyRewards>>(prev: T) => T
    ): void;
  } | null>(null);

export const isAllRewardsEmpty = (rewards: Partial<EmptyRewards> = {}) =>
  Object.values(rewards).every(
    // Only treat values strictly equal to true as empty to prevent false positives
    (isEmpty) => isEmpty === true
  );

/**
 * Hook providing an updater for empty rewards.
 * When used inside EmptyRewardsContext.Provider, it merges your partial updates into the state.
 * Outside the provider, it safely does nothing.
 * @returns A function to apply partial EmptyRewards updates.
 */
export const useUpdateEmptyRewards = () => {
  const setEmptyRewards = useContext(EmptyRewardsContext)?.setEmptyRewards;

  const updateState = useCallback(
    (updates: Partial<EmptyRewards>) => {
      setEmptyRewards?.((prev) => {
        return {
          ...prev,
          ...updates,
        };
      });
    },
    [setEmptyRewards]
  );

  return updateState;
};
